import { FunctionsHttpError, SupabaseClient } from '@supabase/supabase-js';
import { getSupabase, isSupabaseConfigured } from '../config/supabase.js';
import { StorageManager } from '../storage/index.js';
import { SyncOp } from '../storage/outbox.js';

/**
 * How the sync engine reaches the server: one request to the `sync` Edge Function
 * (Docs/SYNC_CONTRACT.md, section 3), as the signed-in learner. The engine takes any function
 * of this shape, so tests hand it the server's own rules instead of a network.
 */

export interface SyncRequestBody {
  device_id: string;
  ops: SyncOp[];
  /** The cursor of the previous answer; null asks for everything. */
  since: string | null;
  /** This device keeps one card per MEANING of a word; without it the server sends first meanings only. */
  senses: true;
  /** Who is asking: the server refuses an extension older than its minimum (a forced update). */
  client?: { app: 'extension' | 'web'; version: string | null };
}

/** The extension (with its manifest version) or the website. */
export function syncClient(): { app: 'extension' | 'web'; version: string | null } {
  const runtime = typeof chrome !== 'undefined' ? chrome.runtime : undefined;
  if (typeof runtime?.getManifest === 'function') {
    return { app: 'extension', version: runtime.getManifest().version ?? null };
  }
  return { app: 'web', version: null };
}

export type SyncOpResult =
  { id: string; ok: true } | { id: string; ok: false; error: string; duplicate_of?: string };

export interface SyncProfileRow {
  native_language: string;
  new_words_per_day: number;
  session_interval_minutes: number;
  cards_per_session: number;
  onboarding_completed_at: string | null;
  /** When the learner's data was last wiped (staging's "start over as a new user"). */
  data_reset_at?: string | null;
  updated_at: string;
}

export interface SyncAnswer {
  results: SyncOpResult[];
  changes: {
    cards: Record<string, unknown>[];
    known_words: Record<string, unknown>[];
    review_logs: Record<string, unknown>[];
    deleted: { kind: 'card' | 'known_word'; id: string }[];
    profile: SyncProfileRow | null;
  };
  cursor: string;
  more: boolean;
  full: boolean;
  /** The oldest extension version the server works with, when there is one. */
  min_extension_version?: string;
  /** The learner's access, as `saveAccess` reads it; absent when the server could not read it. */
  access?: unknown;
}

export type SyncTransport = (body: SyncRequestBody) => Promise<SyncAnswer>;

/**
 * Why a request got no usable answer:
 * - `retry`: no connection, a server error, or no answer in time. Nothing is lost: the same
 *   operations are sent again later.
 * - `signed_out`: there is no session to send with. Tried again when a page next asks.
 * - `refused`: the server could not read the request at all (400) and applied nothing.
 * - `update_required`: this extension is older than the server works with (426); nothing was
 *   applied, the operations wait until the extension is updated.
 */
export type SyncFailure = 'retry' | 'signed_out' | 'refused' | 'update_required';

export class SyncRequestError extends Error {
  constructor(
    public readonly kind: SyncFailure,
    message: string,
    /** With `update_required`: the oldest version the server works with. */
    public readonly minVersion?: string
  ) {
    super(message);
    this.name = 'SyncRequestError';
  }
}

/** The Supabase client with a signed-in session, or a `signed_out` error. */
export async function signedInSupabase(): Promise<SupabaseClient> {
  const profile = await StorageManager.getProfile();
  const key = profile.supabase_publishable_key || profile.supabase_anon_key;
  if (!isSupabaseConfigured(profile.supabase_url, key)) {
    throw new SyncRequestError('signed_out', 'The server is not configured.');
  }
  const supabase = getSupabase(profile.supabase_url, key);
  const { data } = await supabase.auth.getSession();
  if (!data.session) throw new SyncRequestError('signed_out', 'Nobody is signed in.');
  return supabase;
}

function looksLikeAnAnswer(data: unknown): data is SyncAnswer {
  const answer = data as Partial<SyncAnswer> | null;
  return (
    !!answer &&
    Array.isArray(answer.results) &&
    typeof answer.cursor === 'string' &&
    !!answer.changes &&
    Array.isArray(answer.changes.cards) &&
    Array.isArray(answer.changes.known_words) &&
    Array.isArray(answer.changes.review_logs)
  );
}

/** The real transport: the `sync` Edge Function, given up on after `timeoutMs`. */
export function edgeFunctionTransport(timeoutMs: number): SyncTransport {
  return async (body) => {
    const supabase = await signedInSupabase();
    const giveUp = new AbortController();
    const timer = setTimeout(() => giveUp.abort(), timeoutMs);
    try {
      const { data, error } = await supabase.functions.invoke<SyncAnswer>('sync', {
        body,
        signal: giveUp.signal,
      });
      if (error) {
        const status = error instanceof FunctionsHttpError ? error.context?.status : undefined;
        if (status === 400) throw new SyncRequestError('refused', error.message);
        if (status === 426) {
          let min: string | undefined;
          try {
            min = (await (error.context as Response).clone().json())?.min_version;
          } catch {
            min = undefined;
          }
          throw new SyncRequestError('update_required', 'This version is too old.', min);
        }
        if (status === 401 || status === 403) {
          throw new SyncRequestError('signed_out', error.message);
        }
        throw new SyncRequestError('retry', error.message);
      }
      if (!looksLikeAnAnswer(data)) {
        throw new SyncRequestError('retry', 'The server sent an answer that cannot be read.');
      }
      return data;
    } finally {
      clearTimeout(timer);
    }
  };
}
